import { ModelUser, ModelPartner } from "@dia-ui/util_interlink-api";
import React, {ChangeEvent, useCallback, useContext, useEffect, useState} from 'react';
import { Link, useLocation } from 'react-router';
import { AppContext } from "../../context/AppContext";
import { RouteItem, RouteItems } from "../../routes";

/**
 * mirrors $dia-grid-breakpoint-mobile from the design system. below it the sidebar is
 * an off-canvas drawer instead of a 220px column, so "collapsed" means off-screen
 * rather than a 50px icon rail
 */
const MOBILE_QUERY = '(max-width: 992px)';

/**
 * isMobileViewport
 */
const isMobileViewport = (): boolean =>
  typeof window !== 'undefined' && window.matchMedia(MOBILE_QUERY).matches;

/**
 * Nav
 * @constructor
 */
export function Nav() {
  const {
    services: {
      serviceAuth,
      servicePartner,
      servicePartnerFile,
      serviceUser
    },
    I18N,
    getRoutes
  } = useContext(AppContext);

  const ROUTE_CONFIG: RouteItems = getRoutes();
  const location = useLocation();
  const [user, setUser] = useState<ModelUser>();
  const [partner, setPartner] = useState<ModelPartner>();
  const [menuCollapsed, setMenuCollapsed] = useState<boolean>(isMobileViewport);
  const [fileExchangeVisible, setFileExchangeVisible] = useState<boolean>(false);

  /**
   * setUser
   */
  useEffect(() => {
    const storedUser = serviceAuth.getUser();
    if (storedUser) {
      setUser(storedUser);
    }
  }, [serviceAuth]);

  /**
   * setPartner
   */
  useEffect(() => {
    if (user) {
      (async () => {
        const partner = await servicePartner.getById(user.partner_id);

        setPartner(partner);
      })();
    }
  }, [servicePartner, user]);

  /**
   * the file exchange module is booked per partner, so the entry only makes sense
   * when this partner can actually use it: a supplier shares files with it, or it
   * provides files itself
   */
  useEffect(() => {
    if (!user) {
      return;
    }

    let isCurrent = true;

    (async () => {
      try {
        /**
         * both sides stand on their own: one of them being unreachable must not
         * bury the other one's result
         */
        const [groups, provided] = await Promise.all([
          servicePartnerFile.getFiles().catch(() => ({})),
          servicePartnerFile.getProvidedFiles().catch(() => ({ quota: { used: 0, max: 0 }, groups: [] }))
        ]);

        if (isCurrent) {
          /**
           * the same condition the page itself uses for its two views: files a
           * supplier shares, storage booked as a supplier, or files this partner
           * already provides — the last case exists without a booked quota
           */
          setFileExchangeVisible(
            Object.keys(groups).length > 0
            || provided.quota.max > 0
            || provided.groups.some((group) => group.files.length > 0)
          );
        }
      } catch (error) {
        /**
         * a partner without the module gets an error here, which is the normal
         * case and simply leaves the entry hidden
         */
      }
    })();

    return () => {
      isCurrent = false;
    };
  }, [servicePartnerFile, user]);

  /**
   * routeIsHidden
   * every route with nav: true renders generically; only the file exchange is
   * gated, and getRoutes() hands out entries without an id, so the path is the
   * only stable handle to recognize it by
   * @param route
   */
  const routeIsHidden = (route: RouteItem): boolean =>
    route.path === ROUTE_CONFIG.FILE_EXCHANGE.path && !fileExchangeVisible;

  /**
   * imageUrl
   */
  let imageUrl = user
    // @ts-ignore
    ? serviceUser.getUserImage(user.hashId)
    : "/default-avatar.png";

  /**
   * collapse whenever we cross into the mobile breakpoint. below it an expanded
   * sidebar is a full overlay, and arriving on a page behind it hides the content
   */
  useEffect(() => {
    const query = window.matchMedia(MOBILE_QUERY);
    const sync = (event: MediaQueryListEvent) => setMenuCollapsed(event.matches);

    query.addEventListener('change', sync);

    return () => query.removeEventListener('change', sync);
  }, []);

  /**
   * closeDrawer
   */
  const closeDrawer = useCallback((): void => setMenuCollapsed(true), []);

  /**
   * escape closes the mobile drawer, which is the expected way out of an overlay
   */
  useEffect(() => {
    if (menuCollapsed || !isMobileViewport()) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeDrawer();
      }
    };

    window.addEventListener('keydown', onKeyDown);

    return () => window.removeEventListener('keydown', onKeyDown);
  }, [menuCollapsed, closeDrawer]);

  /**
   * setMenuCollapsedState
   * @param event
   */
  const setMenuCollapsedState = (event: ChangeEvent<HTMLInputElement>): void => {
    setMenuCollapsed(event.target.checked);
  };

  return (
    <>
      { /** the drawer's own controls travel off-canvas with it, so the opener lives outside **/ }
      <button
        type="button"
        data-nav-drawer="open"
        aria-expanded={ !menuCollapsed }
        aria-controls="dia-navigation-sidebar-menu"
        onClick={ () => setMenuCollapsed(false) }
      >
        <i data-icon-before="bars" aria-hidden="true"></i>
        <span>{ I18N.t("Menü öffnen") }</span>
      </button>

      { /** the scrim only exists while the drawer is open, so no selector has to
            reach backwards across the DOM to hide it **/ }
      { !menuCollapsed && (
        <button
          type="button"
          data-nav-drawer="scrim"
          tabIndex={ -1 }
          aria-hidden="true"
          onClick={ closeDrawer }
        />
      ) }

    <dia-navigation-sidebar>
      <input type="checkbox" id="dia-navigation-sidebar-toggle" checked={ menuCollapsed } onChange={(event: ChangeEvent<HTMLInputElement>) => setMenuCollapsedState(event)} />
      <label
        htmlFor="dia-navigation-sidebar-toggle"
        title={ menuCollapsed ? I18N.t("Menü ausklappen") : I18N.t("Menü einklappen") }
      >
        <i data-icon-before="arrow-left"></i>
        { menuCollapsed ? I18N.t("Menü ausklappen") : I18N.t("Menü einklappen") }
      </label>
      <button
        type="button"
        data-nav-drawer="close"
        onClick={ closeDrawer }
      >
        <i data-icon-before="xmark" aria-hidden="true"></i>
        <span>{ I18N.t("Menü schließen") }</span>
      </button>
      <ul id="dia-navigation-sidebar-menu">
        <li key={ ROUTE_CONFIG.PERSONAL_DATA.path }>
          <Link to={ ROUTE_CONFIG.PERSONAL_DATA.path } viewTransition>
            <dia-avatar>
              <picture>
                <img src={ imageUrl } alt={ I18N.t("User Image") } />
                <dia-tooltip
                  data-tooltip-position="bottom"
                  data-hover
                  data-nested
                >
                  <img src={ imageUrl } alt={ I18N.t("User Image") } />
                </dia-tooltip>
              </picture>
              <span>
                { user ? `${user.firstname} ${user.lastname}` : I18N.t('Username Missing') }
                <small>{ user ? user.partner_id : '' }</small>
              </span>
              {partner ? (
                <img src={ servicePartner.getCompanyLogoUrl(partner.hashId) } alt={ I18N.t("Company Logo") }/>
              ) : ''}
            </dia-avatar>
          </Link>
        </li>

        { /** iterate through all route and render it if nav is true **/ }
        { Object.values(ROUTE_CONFIG).map((ROUTE: RouteItem) =>
          ROUTE.nav && !routeIsHidden(ROUTE) ? <li data-active={ location.pathname.includes(ROUTE.path) } key={ ROUTE.path }>
            <Link to={ ROUTE.path } title={ ROUTE.title } viewTransition>
              { ROUTE.icon ? <i data-icon-before={ ROUTE.icon }></i> : '' }
              <span>{ ROUTE.title }</span>
            </Link>
          </li> : ''
        ) }
      </ul>
    </dia-navigation-sidebar>
    </>
  );
}
